import { Slider } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { currencyFormatter } from "../../../helpers/currencyFormat";
import { useCalculate, useIsInViewport, useWindowSize } from "../../../hooks";
import { Button } from "../../common";
import Calculations from "./Calculations";
import "./Calculator.scss";

const fundingValueMax = 15000;
const fundingValueMin = 5000;
const startingSalaryMax = 150000;
const startingSalaryMin = 30000;

const Calculator = ({ title, short }) => {
  const [fundingValue, setFundingValue] = useState(10000);
  const [startingSalary, setStartingSalary] = useState(70000);
  const [calculationsHidden, setCalculationsHidden] = useState(true);
  const [formData, setFormData] = useState();

  const windowSize = useWindowSize();
  const isInViewportThreshold = windowSize.width > 768 ? 0.5 : 0.2;
  const sectionRef = useRef(null);
  const isInViewport = useIsInViewport(sectionRef, isInViewportThreshold);
  const [showAnimation, setShowAnimation] = useState(false);

  const { fundingData, recalculate, isCalculating } = useCalculate(fundingValue, startingSalary);

  useEffect(() => {
    if (isInViewport) {
      setTimeout(() => setShowAnimation(isInViewport), 50);
    }
  }, [isInViewport]);

  const handleFormSubmit = useCallback(async () => {
    setCalculationsHidden(false);
    await recalculate();
  }, [recalculate]);

  useEffect(() => {
    if (fundingData) {
      setFormData({
        apr: fundingData.effectiveApr,
        effectiveTerm: fundingData.effectiveTermToPay,
        averageMonthlyPayment: fundingData.avgMonthlyPayment,
        totalCosts: fundingData.financeCharge,
      });
    }
  }, [fundingData]);

  return (
    <section
      className={`section section--calculator${
        showAnimation ? " onScreen" : ""
      }`}
      ref={sectionRef}
    >
      <div className="section__innerWrapper">
        <article className="overDeco">
          <h2 className="center animateIn">
            <a className="anchor animateIn" name="calculator">
              CALCULATOR
            </a>
          </h2>
          <h3 className="center animateIn">{title}</h3>
        </article>
        <div className="calculatorTool animateIn overDeco">
          <div className="step1">
            <div className="step1__slider-wrapper">
              <div className="left">
                <span className="hint">{short}</span>
                <Slider
                  aria-label="Default"
                  valueLabelDisplay="off"
                  value={fundingValue}
                  onChange={(e) => {
                    setCalculationsHidden(true);
                    setFundingValue(e.target.value);
                  }}
                  min={fundingValueMin}
                  max={fundingValueMax}
                  step={1000}
                  sx={{ color: "rgb(246,220,28)" }}
                />
                <div className="sliderMinMaxHint font-weight-medium">
                  <span className="minValue">{currencyFormatter(5000)}</span>
                  <span className="maxValue">
                    {currencyFormatter(fundingValueMax)}
                  </span>
                </div>
              </div>
              <div className="right">
                <div className="decorationIntersect">
                  {/* <span className="yellow">
                    <span
                      className="bubble blue"
                      style={{
                        transform: `translate(50%, -50%) scale(${
                          0.95 + fundingValue / fundingValueMax
                        })`,
                      }}
                    ></span>
                  </span> */}
                  <span
                    className="bubble lightblue"
                    style={{
                      transform: `translate(50%, -50%) scale(${
                        fundingValue === fundingValueMax
                          ? 2
                          : fundingValue === fundingValueMin
                          ? 1.2
                          : 1 + fundingValue / fundingValueMax
                      })`,
                    }}
                  ></span>
                </div>
                <span className="rangeSliderValue">
                  {currencyFormatter(fundingValue)}
                </span>
              </div>
            </div>
            <div className="step1__slider-wrapper">
              <div className="left">
                <span className="hint">Expected Starting Salary</span>
                <Slider
                  aria-label="Default"
                  valueLabelDisplay="off"
                  value={startingSalary}
                  onChange={(e) => {
                    setCalculationsHidden(true);
                    setStartingSalary(e.target.value);
                  }}
                  min={startingSalaryMin}
                  max={startingSalaryMax}
                  step={1000}
                  sx={{ color: "rgb(246,220,28)" }}
                />
                <div className="sliderMinMaxHint font-weight-medium">
                  <span className="minValue">{currencyFormatter(30000)}</span>
                  <span className="maxValue">
                    {currencyFormatter(startingSalaryMax)}
                  </span>
                </div>
              </div>
              <div className="right">
                <div className="decorationIntersect">
                  <span
                    className="bubble lightblue"
                    style={{
                      transform: `translate(50%, -50%) scale(${
                        startingSalary === startingSalaryMax
                          ? 2
                          : startingSalary === startingSalaryMin
                          ? 1.2
                          : 1 + startingSalary / startingSalaryMax
                      })`,
                    }}
                  ></span>
                </div>
                <span className="rangeSliderValue">
                  {currencyFormatter(startingSalary)}
                </span>
              </div>
            </div>
          </div>
          <Button
            className="primary full-width"
            id="calculate"
            onClick={handleFormSubmit}
          >
            Calculate
          </Button>
          <div className={`step3 ${calculationsHidden ? "hidden" : "active"}`}>
            <Calculations {...formData} isLoading={isCalculating} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
